import { type ReactNode, useEffect, useState } from 'react'
import type { Icon } from '@phosphor-icons/react'
import { PencilSimpleIcon, XIcon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'

/** The one frame every inspector panel lives in: the node's icon and name, an optional header link
 *  ("Change" on the trigger), close, then a divider-separated body. Shared by the trigger picker
 *  and every node panel so nothing that configures a node ever gets a different width or header.
 *
 *  With `onRename` the title is editable in place: click it, type, Enter or blur commits, Escape
 *  cancels, and clearing it hands back an empty string so the caller can fall back to its own
 *  inferred name (which `renamePlaceholder` shows while the field is empty). */
export function InspectorShell({
  icon: HeaderIcon,
  title,
  subtitle = `Settings`,
  action,
  onRename,
  renamePlaceholder,
  onClose,
  children,
}: {
  icon: Icon
  title: string
  subtitle?: string
  action?: { label: string; onClick: () => void }
  onRename?: (name: string) => void
  renamePlaceholder?: string
  onClose: () => void
  children: ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(title)
  // A different node under the same shell must not keep the previous node's half-typed name.
  useEffect(() => {
    setEditing(false)
    setDraft(title)
  }, [title])

  const commit = () => {
    setEditing(false)
    if (draft.trim() !== title) onRename?.(draft.trim())
  }

  return (
    // A 384px column leaves a phone's canvas 0px wide, so below md the panel covers the canvas instead.
    <aside className="absolute inset-0 z-40 flex flex-col bg-surface md:static md:z-auto md:w-96 md:shrink-0 md:border-l md:border-default">
      <header className="flex items-center justify-between border-b border-default px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-subtle">
            <HeaderIcon size={17} weight="bold" />
          </span>
          <div className="min-w-0 flex-1">
            {editing ? (
              <input
                autoFocus
                value={draft}
                placeholder={renamePlaceholder}
                aria-label="Name"
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === `Enter`) commit()
                  if (e.key === `Escape`) {
                    setDraft(title)
                    setEditing(false)
                  }
                }}
                className="-mx-1 w-full rounded border border-blue-400 px-1 text-sm font-semibold text-default focus:outline-none"
              />
            ) : onRename ? (
              <Button
                variant="ghost"
                size="inline"
                onClick={() => setEditing(true)}
                title="Rename"
                className="group max-w-full justify-start"
              >
                <span className="truncate text-sm font-semibold">{title}</span>
                <PencilSimpleIcon
                  size={12}
                  className="size-3 text-minimal opacity-0 transition-opacity group-hover:opacity-100"
                />
              </Button>
            ) : (
              <div className="truncate text-sm font-semibold text-default">{title}</div>
            )}
            <div className="truncate text-[11px] text-subtle">{subtitle}</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {action && (
            <Button variant="link" size="xs" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          <Button
            variant="subtle"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close settings"
            className="size-11 md:size-7"
          >
            <XIcon size={16} weight="bold" />
          </Button>
        </div>
      </header>
      <div className="flex-1 divide-y divide-subtle overflow-y-auto px-4 py-4">{children}</div>
    </aside>
  )
}
